// Constants for canvas icon generation
const CANVAS_CONFIG = {
	HEADING_BUCKET_SIZE: 15, // Degrees - reduces memory usage (24 buckets instead of 360)
	PIXEL_RATIO: 2,
	ICON_SIZE_MULTIPLIER: 0.8,
	ICON_VERTICAL_OFFSET: -5, // Pixels to adjust icon positioning
	HEADING_LINE_SCALE: 2,
	HEADING_LINE_BASE_OFFSET: -20, // Distance from center to arrow base
	RGBA_CHANNELS: 4 // Red, Green, Blue, Alpha
};

const HEADING_LINE_CONFIG = {
	SPRITE_ID: "heading_line",
	ROTATION_OFFSET: Math.PI // π radians - sprite artwork faces DOWN, need to point UP
};

/**
 * Calculates heading bucket to reduce memory usage
 * @param {number|null} heading - Raw heading value in degrees
 * @param {boolean} showHeadingLine - Whether heading line should be shown
 * @returns {number|null} Rounded heading value or null
 */
const calculateHeadingBucket = (heading, showHeadingLine) => {
	if (!showHeadingLine || heading === null) return null;
	return Math.round(heading / CANVAS_CONFIG.HEADING_BUCKET_SIZE) * CANVAS_CONFIG.HEADING_BUCKET_SIZE;
};

/**
 * Generates unique image name for canvas icon
 * @param {string} icon - Icon identifier
 * @param {string} frame - Frame identifier
 * @param {number|null} headingBucket - Heading bucket value
 * @returns {string} Unique image name
 */
const generateImageName = (icon, frame, headingBucket) => {
	const baseImageName = `${icon}-${frame}`;
	return headingBucket !== null ? `${baseImageName}-heading-${headingBucket}` : baseImageName;
};

/**
 * Calculates canvas dimensions accounting for heading line extension
 * @param {Object} frameData - Frame sprite data
 * @param {Object|null} headingLineData - Heading line sprite data
 * @param {boolean} showHeadingLine - Whether heading line should be shown
 * @returns {Object} Object containing width and height
 */
const calculateCanvasDimensions = (frameData, headingLineData, showHeadingLine) => {
	const headingLineExtension = showHeadingLine && headingLineData ? headingLineData.height : 0;

	return {
		width: frameData.width + headingLineExtension,
		height: frameData.height + headingLineExtension
	};
};

/**
 * Calculates center position for frame on expanded canvas
 * @param {number} canvasWidth - Total canvas width
 * @param {number} canvasHeight - Total canvas height
 * @param {number} frameWidth - Frame width
 * @param {number} frameHeight - Frame height
 * @returns {Object} Object containing centerX and centerY
 */
const calculateFrameCenter = (canvasWidth, canvasHeight, frameWidth, frameHeight) => ({
	centerX: (canvasWidth - frameWidth) / 2,
	centerY: (canvasHeight - frameHeight) / 2
});

/**
 * Calculates icon positioning within the frame
 * @param {number} frameCenterX - Frame center X coordinate
 * @param {number} frameCenterY - Frame center Y coordinate
 * @param {number} frameWidth - Frame width
 * @param {number} frameHeight - Frame height
 * @param {number} iconWidth - Icon width
 * @param {number} iconHeight - Icon height
 * @returns {Object} Object containing icon position coordinates
 */
const calculateIconPosition = (frameCenterX, frameCenterY, frameWidth, frameHeight, iconWidth, iconHeight) => {
	const scaledIconWidth = iconWidth * CANVAS_CONFIG.ICON_SIZE_MULTIPLIER;
	const scaledIconHeight = iconHeight * CANVAS_CONFIG.ICON_SIZE_MULTIPLIER;

	return {
		x: frameCenterX + (frameWidth - scaledIconWidth) / 2,
		y: frameCenterY + (frameHeight - scaledIconHeight) / 2 + CANVAS_CONFIG.ICON_VERTICAL_OFFSET,
		width: scaledIconWidth,
		height: scaledIconHeight
	};
};

/**
 * Draws heading line on canvas context
 * @param {CanvasRenderingContext2D} context - Canvas rendering context
 * @param {Object} headingLineData - Heading line sprite data
 * @param {number} headingBucket - Heading bucket value in degrees
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @param {HTMLImageElement} spriteImage - Sprite image containing all icons
 */
const drawHeadingLine = (context, headingLineData, headingBucket, canvasWidth, canvasHeight, spriteImage) => {
	const { x, y, width, height } = headingLineData;

	context.save();

	// Move origin to canvas center
	context.translate(canvasWidth / 2, canvasHeight / 2);

	// Calculate rotation: convert degrees to radians and add offset for sprite orientation
	const rotationAngle = (headingBucket * Math.PI) / 180 + HEADING_LINE_CONFIG.ROTATION_OFFSET;
	context.rotate(rotationAngle);

	// Calculate scaled dimensions
	const scaledWidth = width * CANVAS_CONFIG.HEADING_LINE_SCALE;
	const scaledHeight = height * CANVAS_CONFIG.HEADING_LINE_SCALE;

	// Draw arrow with base at center, tip pointing outward
	context.drawImage(
		spriteImage,
		x,
		y,
		width,
		height, // Source rectangle
		-scaledWidth / 2, // Center horizontally
		CANVAS_CONFIG.HEADING_LINE_BASE_OFFSET, // Base of arrow at center
		scaledWidth,
		scaledHeight
	);

	context.restore();
};

/**
 * Draws frame on canvas context
 * @param {CanvasRenderingContext2D} context - Canvas rendering context
 * @param {Object} frameData - Frame sprite data
 * @param {number} frameCenterX - Frame center X coordinate
 * @param {number} frameCenterY - Frame center Y coordinate
 * @param {HTMLImageElement} spriteImage - Sprite image containing all icons
 */
const drawFrame = (context, frameData, frameCenterX, frameCenterY, spriteImage) => {
	const { x, y, width, height } = frameData;

	context.drawImage(
		spriteImage,
		x,
		y,
		width,
		height, // Source rectangle
		frameCenterX,
		frameCenterY,
		width,
		height // Destination rectangle
	);
};

/**
 * Draws icon on canvas context
 * @param {CanvasRenderingContext2D} context - Canvas rendering context
 * @param {Object} iconData - Icon sprite data
 * @param {Object} iconPosition - Icon position and dimensions
 * @param {HTMLImageElement} spriteImage - Sprite image containing all icons
 */
const drawIcon = (context, iconData, iconPosition, spriteImage) => {
	const { x: iconX, y: iconY, width: iconWidth, height: iconHeight } = iconData;

	context.drawImage(
		spriteImage,
		iconX,
		iconY,
		iconWidth,
		iconHeight, // Source rectangle
		iconPosition.x,
		iconPosition.y,
		iconPosition.width,
		iconPosition.height // Destination rectangle
	);
};

/**
 * Creates canvas icon overlay generator with rendering logic
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @param {Object} frameData - Frame sprite data
 * @param {Object} iconData - Icon sprite data
 * @param {Object|null} headingLineData - Heading line sprite data
 * @param {number|null} headingBucket - Heading bucket value
 * @param {boolean} showHeadingLine - Whether to show heading line
 * @param {HTMLImageElement} spriteImage - Sprite image
 * @returns {Object} Canvas overlay object with Mapbox GL JS interface
 */
const createCanvasOverlay = (
	canvasWidth,
	canvasHeight,
	frameData,
	iconData,
	headingLineData,
	headingBucket,
	showHeadingLine,
	spriteImage
) => ({
	width: canvasWidth,
	height: canvasHeight,
	data: new Uint8Array(canvasWidth * canvasHeight * CANVAS_CONFIG.RGBA_CHANNELS),
	renderingNeeded: true,

	onAdd: function () {
		const canvas = document.createElement("canvas");
		canvas.width = this.width;
		canvas.height = this.height;
		this.context = canvas.getContext("2d");
	},

	render: function () {
		if (!this.renderingNeeded || !this.context) return false;

		const context = this.context;
		const { width: frameWidth, height: frameHeight } = frameData;
		const { width: iconWidth, height: iconHeight } = iconData;

		// Clear canvas
		context.clearRect(0, 0, this.width, this.height);

		// Calculate positioning
		const frameCenter = calculateFrameCenter(this.width, this.height, frameWidth, frameHeight);
		const iconPosition = calculateIconPosition(
			frameCenter.centerX,
			frameCenter.centerY,
			frameWidth,
			frameHeight,
			iconWidth,
			iconHeight
		);

		// Draw heading line first (behind frame and icon)
		if (showHeadingLine && headingLineData && headingBucket !== null) {
			drawHeadingLine(context, headingLineData, headingBucket, this.width, this.height, spriteImage);
		}

		// Draw frame
		drawFrame(context, frameData, frameCenter.centerX, frameCenter.centerY, spriteImage);

		// Draw icon on top of frame
		drawIcon(context, iconData, iconPosition, spriteImage);

		// Update image data
		this.data = context.getImageData(0, 0, this.width, this.height).data;
		this.renderingNeeded = false;

		return true;
	}
});

/**
 * Validates that required sprite data exists
 * @param {Object} frameData - Frame sprite data
 * @param {Object} iconData - Icon sprite data
 * @returns {boolean} True if required data exists
 */
const validateSpriteData = (frameData, iconData) => !!(frameData && iconData);

/**
 * Creates canvas map images with icon frame overlays and optional heading lines
 *
 * This function generates composite canvas images combining:
 * 1. Optional heading line (drawn first, behind other elements)
 * 2. Frame border/background
 * 3. Icon symbol (drawn last, on top)
 *
 * Uses heading buckets (15° increments) to optimize memory usage
 *
 * @param {string} icon - Icon identifier from sprite data
 * @param {string} frame - Frame identifier from sprite data
 * @param {Object} map - Mapbox GL JS map instance
 * @param {number|null} heading - Heading value in degrees (0-359)
 * @param {boolean} showHeadingLine - Whether to include heading line in composite
 * @returns {boolean} True if image was created/already exists
 */
export const canvasIconFrameOverlayGenerator = (icon, frame, map, heading = null, showHeadingLine = false) => {
	// Get sprite data from global objects
	const spriteImage = global.OC_spriteImage;
	const frameData = global.OC_spriteData?.[frame];
	const iconData = global.OC_spriteData?.[icon];
	const headingLineData = showHeadingLine ? global.OC_spriteData?.[HEADING_LINE_CONFIG.SPRITE_ID] : null;

	// Validate required data exists
	if (!map || !validateSpriteData(frameData, iconData)) {
		return false;
	}

	// Calculate heading bucket and generate image name
	const headingBucket = calculateHeadingBucket(heading, showHeadingLine);
	const imageName = generateImageName(icon, frame, headingBucket);

	// Skip if image already exists
	if (map.hasImage(imageName)) {
		return true;
	}

	// Calculate canvas dimensions
	const canvasDimensions = calculateCanvasDimensions(frameData, headingLineData, showHeadingLine);

	// Create and add canvas overlay to map
	const canvasOverlay = createCanvasOverlay(
		canvasDimensions.width,
		canvasDimensions.height,
		frameData,
		iconData,
		headingLineData,
		headingBucket,
		showHeadingLine,
		spriteImage
	);

	map.addImage(imageName, canvasOverlay, { pixelRatio: CANVAS_CONFIG.PIXEL_RATIO });

	return true;
};
